import { useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { requestMagicLink, verify } from '../lib/api';

interface Props {
  onVerify: (c: Customer) => void;
}

export function Login({ onVerify }: Props) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'enter' | 'sent'>('enter');
  const [error, setError] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    const res = await requestMagicLink(email.trim().toLowerCase());
    if (res.ok) {
      setDevLink(res.devMagicLink ?? null);
      setPhase('sent');
    } else {
      setError(res.error ?? 'Something went wrong');
    }
  }

  async function followDevLink() {
    if (!devLink) return;
    const url = new URL(devLink);
    const token = url.searchParams.get('token') ?? '';
    const r = await verify(token);
    if (r.ok && r.customer) onVerify(r.customer);
    else setError(r.error ?? 'Verify failed');
  }

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        CREATOR PORTAL
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        Sign in
      </h1>
      <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 8, maxWidth: 480, lineHeight: 1.5 }}>
        Enter the email on your creator account. You'll get a one-tap link.
        New creators are provisioned manually by the Set Apart team — reach out
        to <a href="mailto:hi@setapart.app" style={{ color: COLORS.accent }}>hi@setapart.app</a>.
      </p>

      {phase === 'enter' && (
        <form onSubmit={onSubmit} style={{ marginTop: 20, maxWidth: 360 }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={inputStyle}
          />
          {error && <div style={{ color: COLORS.accent, fontSize: 12, marginTop: 6 }}>{error}</div>}
          <button type="submit" style={primaryBtn}>Send magic link</button>
        </form>
      )}

      {phase === 'sent' && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontFamily: FONTS.display, fontSize: 22, color: COLORS.ink, marginBottom: 8, fontStyle: 'italic' }}>
            Check your inbox
          </div>
          <p style={{ fontSize: 13, color: COLORS.ink, maxWidth: 460 }}>
            Open the link sent to <strong>{email}</strong>. Expires in 15 minutes.
          </p>
          {devLink && (
            <div style={devBox}>
              <div style={{ fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 4 }}>
                DEV MODE
              </div>
              <button onClick={followDevLink} style={primaryBtn}>Open dev magic link</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 10,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: COLORS.muted,
  marginBottom: 6,
};
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 12,
  border: `1px solid ${COLORS.sand}`,
  background: '#fff',
  fontFamily: FONTS.body,
  fontSize: 14,
  color: COLORS.ink,
};
const primaryBtn: CSSProperties = {
  marginTop: 14,
  padding: '12px 20px',
  borderRadius: 999,
  background: COLORS.ink,
  color: COLORS.cream,
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const devBox: CSSProperties = {
  marginTop: 16,
  padding: 14,
  background: 'rgba(184,71,46,0.06)',
  border: `1px dashed ${COLORS.accent}`,
  borderRadius: 12,
  maxWidth: 460,
};
